import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { requestPosters } from 'store/actions/posters';

import { useSettings } from './useSettings';

export const usePosters = () => {
	const dispatch = useDispatch();
	const [settingsLoading, settings] = useSettings();
	const posters = useSelector(state => state.posters.posters);
	const sessionPosters = useSelector(state => state.posters.sessionPosters);
	const postersFetched = useSelector(state => state.posters.postersFetched);
	const ignoreAbIds = useSelector(state => state.modes?.siteConfig?.ignore_ab_ids);

	const loading = settingsLoading || !postersFetched;

	useEffect(() => {
		if (settingsLoading || postersFetched) return;
		dispatch(requestPosters({ source: settings.source, conferenceId: settings.conferenceId }));
	}, [settingsLoading]);

	// Scope out abstracts whose ab_id (poster.id) is listed in the
	// `ignore_ab_ids` site config key. Applied at the root so every
	// tab/mode consuming usePosters() respects it.
	const [visiblePosters, visibleSessionPosters] = useMemo(() => {
		// Posters aren't loaded yet (posters: null, sessionPosters: Map) or no
		// ignore list configured -> pass through untouched.
		if (
			!Array.isArray(ignoreAbIds) ||
			!ignoreAbIds.length ||
			!Array.isArray(posters)
		) {
			return [posters, sessionPosters];
		}
		const ignored = new Set(ignoreAbIds.map(String));
		const filteredPosters = posters.filter(poster => !ignored.has(String(poster.id)));
		const filteredSessionPosters = (Array.isArray(sessionPosters) ? sessionPosters : [])
			.map(session => ({
				...session,
				posters: (session.posters || []).filter(poster => !ignored.has(String(poster.id))),
			}))
			.filter(session => session.posters.length);
		return [filteredPosters, filteredSessionPosters];
	}, [posters, sessionPosters, ignoreAbIds]);

	return [loading, visiblePosters, visibleSessionPosters];
};
